import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { 
  Ship, 
  Anchor,
  Loader2,
  Plus,
  Search,
  MapPin,
  Container as ContainerIcon,
  Edit,
  Trash2,
  RefreshCw,
  Flag,
  Calendar,
  Ruler,
  X,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

const SERVICE_ROUTES = [
  { value: 'AE1', label: 'Asia-Europe AE1' },
  { value: 'AE2', label: 'Asia-Europe AE2' },
  { value: 'AE7', label: 'Asia-Europe AE7' },
  { value: 'TP3', label: 'Trans-Pacific TP3' },
  { value: 'TP6', label: 'Trans-Pacific TP6' },
  { value: 'FAL1', label: 'Far East–Mediterranean Loop 1' },
  { value: 'FAL3', label: 'Far East–Mediterranean Loop 3' },
  { value: 'LL1', label: 'Long Beach Loop' },
  { value: 'Jade', label: 'Baltic Jade Service' },
  { value: 'AEU1', label: 'Asia-Europe Union 1' }
];

const VESSEL_FLAGS = [
  'Panama', 'Liberia', 'Marshall Islands', 'Hong Kong', 'Singapore', 
  'Bahamas', 'Malta', 'UK', 'Denmark', 'France', 'Germany', 'China', 'Japan'
];

function AdminFleet() {
  const [vessels, setVessels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingVessel, setEditingVessel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [stats, setStats] = useState(null);
  
  const [formData, setFormData] = useState({
    name: '',
    imo_number: '',
    flag: 'Panama',
    capacity_teu: 20000,
    year_built: 2020,
    length_m: 400,
    beam_m: 59,
    status: 'active',
    service_route: '',
    current_route_name: ''
  });

  useEffect(function() {
    loadVessels();
    loadStats();
  }, []);

  async function loadVessels() {
    setLoading(true);
    try {
      const response = await api.get('/admin/fleet');
      setVessels(response.data);
    } catch (err) {
      console.error('Failed to load vessels:', err);
      toast.error('Failed to load fleet data');
    } finally {
      setLoading(false);
    }
  }

  async function loadStats() {
    try {
      const response = await api.get('/admin/fleet/stats/summary');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to load stats:', err);
    }
  }

  function handleChange(field, value) {
    setFormData(function(prev) {
      const newData = { ...prev, [field]: value };
      // Auto-fill route name
      if (field === 'service_route') {
        const route = SERVICE_ROUTES.find(function(r) { return r.value === value; });
        if (route) {
          newData.current_route_name = route.label;
        }
      }
      return newData;
    });
  }

  function openCreateModal() {
    setEditingVessel(null);
    setFormData({
      name: '',
      imo_number: '',
      flag: 'Panama',
      capacity_teu: 20000,
      year_built: new Date().getFullYear(),
      length_m: 400,
      beam_m: 59,
      status: 'active',
      service_route: '',
      current_route_name: ''
    });
    setShowModal(true);
  }

  function openEditModal(vessel) {
    setEditingVessel(vessel);
    setFormData({
      name: vessel.name || '',
      imo_number: vessel.imo_number || '',
      flag: vessel.flag || 'Panama',
      capacity_teu: vessel.capacity_teu || 20000,
      year_built: vessel.year_built || 2020,
      length_m: vessel.length_m || 400,
      beam_m: vessel.beam_m || 59,
      status: vessel.status || 'active',
      service_route: vessel.service_route || '',
      current_route_name: vessel.current_route_name || ''
    });
    setShowModal(true);
  }

  async function handleSubmit(e) {
    e.preventDefault();
    
    if (!formData.name || !formData.imo_number) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      if (editingVessel) {
        await api.put('/admin/fleet/' + editingVessel.id, formData);
        toast.success('Vessel updated successfully');
      } else {
        await api.post('/admin/fleet', formData);
        toast.success('Vessel created successfully');
      }
      setShowModal(false);
      loadVessels();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save vessel');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(vesselId) {
    try {
      await api.delete('/admin/fleet/' + vesselId);
      toast.success('Vessel deleted successfully');
      setDeleteConfirm(null);
      loadVessels();
      loadStats();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete vessel');
    }
  }

  const filteredVessels = vessels.filter(function(vessel) {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (vessel.name || '').toLowerCase().includes(term) ||
      (vessel.imo_number || '').includes(term) ||
      (vessel.flag || '').toLowerCase().includes(term);
  });

  const activeVessels = vessels.filter(function(v) { return v.status === 'active'; }).length;
  const maintenanceVessels = vessels.filter(function(v) { return v.status === 'maintenance'; }).length;
  const totalCapacity = vessels.reduce(function(sum, v) { return sum + (v.capacity_teu || 0); }, 0);

  return (
    <div className="space-y-6" data-testid="admin-fleet">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-[32px] sv-display text-ink">Fleet</h1>
          <p className="text-gray-600 mt-1">Monitor and manage your vessel fleet</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm" onClick={loadVessels}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            className="bg-ink hover:bg-ink-800 text-white rounded-full"
            onClick={openCreateModal}
          >
            <Plus className="mr-2 h-4 w-4" /> Add Vessel
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-50 rounded-sm flex items-center justify-center">
              <Ship className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{vessels.length}</p>
              <p className="text-sm text-gray-500">Total vessels</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-green-50 rounded-sm flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{activeVessels}</p>
              <p className="text-sm text-gray-500">Active</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-amber-50 rounded-sm flex items-center justify-center">
              <AlertCircle className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{maintenanceVessels}</p>
              <p className="text-sm text-gray-500">Maintenance</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-sm shadow-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-blue-100 rounded-sm flex items-center justify-center">
              <ContainerIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-normal tracking-[-0.01em] text-ink">{totalCapacity.toLocaleString()}</p>
              <p className="text-sm text-gray-500">Total TEU</p>
            </div>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-sm shadow-card p-4">
        <div className="relative">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
          <Input
            placeholder="Search vessels by name, IMO number, or flag..."
            className="pl-12 h-12 rounded-sm"
            value={searchTerm}
            onChange={function(e) { setSearchTerm(e.target.value); }}
            data-testid="vessel-search"
          />
        </div>
      </div>

      {/* Vessels Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      ) : filteredVessels.length > 0 ? (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredVessels.map(function(vessel) {
            return (
              <div key={vessel.id} className="bg-white rounded-sm shadow-card p-6 transition-shadow">
                <div className="flex items-center justify-between mb-4">
                  <div className={"w-12 h-12 rounded-sm flex items-center justify-center " + (vessel.status === 'active' ? 'bg-green-100' : 'bg-amber-100')}>
                    <Ship className={"h-6 w-6 " + (vessel.status === 'active' ? 'text-green-600' : 'text-amber-600')} />
                  </div>
                  <Badge className={vessel.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                    {vessel.status}
                  </Badge>
                </div>
                
                <h3 className="text-xl font-medium tracking-[-0.01em] text-ink mb-1">{vessel.name}</h3>
                <p className="text-sm text-gray-500 sv-mono mb-4">IMO: {vessel.imo_number}</p>
                
                <div className="space-y-2 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-3 text-sm">
                    <ContainerIcon className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{(vessel.capacity_teu || 0).toLocaleString()} TEU capacity</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Flag className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{vessel.flag}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Calendar className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">Built {vessel.year_built}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Ruler className="h-4 w-4 text-gray-400" />
                    <span className="text-gray-600">{vessel.length_m}m × {vessel.beam_m}m</span>
                  </div>
                  {vessel.current_route_name && (
                    <div className="flex items-center gap-3 text-sm">
                      <MapPin className="h-4 w-4 text-gray-400" />
                      <span className="text-gray-600">{vessel.current_route_name}</span>
                    </div>
                  )}
                </div>
                
                <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full"
                    onClick={function() { openEditModal(vessel); }}
                  >
                    <Edit className="h-4 w-4 mr-1" /> Edit
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full text-red-600 hover:bg-red-50"
                    onClick={function() { setDeleteConfirm(vessel); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white rounded-sm shadow-card p-12 text-center">
          <Anchor className="h-12 w-12 text-gray-300 mx-auto mb-4" />
          <p className="text-gray-500 mb-4">No vessels found</p>
          <Button onClick={openCreateModal} className="bg-ink hover:bg-ink-800 rounded-sm">
            <Plus className="h-4 w-4 mr-2" /> Add Your First Vessel
          </Button>
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-sm flex items-center justify-center">
                <Ship className="h-5 w-5 text-blue-600" />
              </div>
              {editingVessel ? 'Edit Vessel' : 'Add New Vessel'}
            </DialogTitle>
          </DialogHeader>

          <form onSubmit={handleSubmit}>
            <div className="grid grid-cols-2 gap-4 py-4">
              <div className="space-y-2">
                <Label>Vessel Name *</Label>
                <Input
                  placeholder="e.g., Südvik Aurora"
                  value={formData.name}
                  onChange={function(e) { handleChange('name', e.target.value); }}
                  className="h-11 rounded-sm"
                  data-testid="vessel-name"
                />
              </div>
              <div className="space-y-2">
                <Label>IMO Number *</Label>
                <Input
                  placeholder="e.g., 9839430"
                  value={formData.imo_number}
                  onChange={function(e) { handleChange('imo_number', e.target.value); }}
                  className="h-11 rounded-sm sv-mono"
                  data-testid="vessel-imo"
                />
              </div>
              <div className="space-y-2">
                <Label>Flag</Label>
                <Select value={formData.flag} onValueChange={function(v) { handleChange('flag', v); }}>
                  <SelectTrigger className="h-11 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {VESSEL_FLAGS.map(function(flag) {
                      return <SelectItem key={flag} value={flag}>{flag}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Status</Label>
                <Select value={formData.status} onValueChange={function(v) { handleChange('status', v); }}>
                  <SelectTrigger className="h-11 rounded-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="maintenance">Maintenance</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Capacity (TEU)</Label>
                <Input
                  type="number"
                  min="100"
                  value={formData.capacity_teu}
                  onChange={function(e) { handleChange('capacity_teu', parseInt(e.target.value) || 0); }}
                  className="h-11 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Year built</Label>
                <Input
                  type="number"
                  min="1990"
                  max={new Date().getFullYear() + 2}
                  value={formData.year_built}
                  onChange={function(e) { handleChange('year_built', parseInt(e.target.value) || 2020); }}
                  className="h-11 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Length (m)</Label>
                <Input
                  type="number"
                  min="100"
                  step="0.1"
                  value={formData.length_m}
                  onChange={function(e) { handleChange('length_m', parseFloat(e.target.value) || 0); }}
                  className="h-11 rounded-sm"
                />
              </div>
              <div className="space-y-2">
                <Label>Beam (m)</Label>
                <Input
                  type="number"
                  min="20"
                  step="0.1"
                  value={formData.beam_m}
                  onChange={function(e) { handleChange('beam_m', parseFloat(e.target.value) || 0); }}
                  className="h-11 rounded-sm"
                />
              </div>
              <div className="space-y-2 col-span-2">
                <Label>Service route</Label>
                <Select value={formData.service_route || "none"} onValueChange={function(v) { handleChange('service_route', v === "none" ? "" : v); }}>
                  <SelectTrigger className="h-11 rounded-sm">
                    <SelectValue placeholder="Select a route" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">No route assigned</SelectItem>
                    {SERVICE_ROUTES.map(function(route) {
                      return <SelectItem key={route.value} value={route.value}>{route.label}</SelectItem>;
                    })}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={function() { setShowModal(false); }} className="rounded-sm">
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-ink hover:bg-ink-800 rounded-sm">
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                ) : (
                  <><CheckCircle className="h-4 w-4 mr-2" /> {editingVessel ? 'Update Vessel' : 'Create Vessel'}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={!!deleteConfirm} onOpenChange={function() { setDeleteConfirm(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3 text-red-600">
              <AlertCircle className="h-6 w-6" />
              Delete Vessel
            </DialogTitle>
          </DialogHeader>
          <p className="text-gray-600 py-4">
            Are you sure you want to delete <strong>{deleteConfirm?.name}</strong>? This action cannot be undone.
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={function() { setDeleteConfirm(null); }} className="rounded-sm">
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={function() { handleDelete(deleteConfirm.id); }}
              className="rounded-full"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AdminFleet;
